import { Button } from '@creditornot/cb-button';
import { Calendar, CaretDown } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { DecodedValueMap } from 'use-query-params';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';

import DeliveryTypeSwitcher from './DeliveryTypeSwitcher';
import DownloadCSV from './DownloadCSV';
import FilterDateRangePicker, { Period } from './FilterDateRangePicker';
import GetReceipts from './GetReceipts';
import { PurchasesQueryConfig } from './purchasesQueryConfig';
import {
  telemetryMerchantAdminPurchasesInteractionDownloadCsv,
  telemetryMerchantAdminPurchasesInteractionGetReceipts,
} from './telemetry';

type HeaderActionsProps = {
  venue: Venue;
  dateRange: {
    from: string;
    to: string;
  };
  handleDateRangeChange: (period: Period) => void;
  setIsBarCodesModalOpen: (isOpen: boolean) => void;
  isDateRangeIntersectsWithAllowedRange: boolean;
  query: DecodedValueMap<PurchasesQueryConfig>;
  isRetail: boolean;
};

const HeaderActionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  height: 100%;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    gap: 16px;
    flex-direction: column;
  }
`;

const HeaderButtons = styled.div`
  gap: 16px;
  display: flex;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    > * {
      flex-grow: 1;
    }
  }
`;

const DateRangePickerInput = styled(Input)`
  min-width: 230px;
`;

const DownloadButton = styled(Button)`
  @media (width <= ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const DownloadMenuContainer = styled.div`
  padding: 0 8px;
  min-width: 160px;
`;

const StyledGetReceipts = styled(GetReceipts)`
  width: 304px;
  padding: 8px;
`;

const StyledDownloadCSV = styled(DownloadCSV)`
  width: 352px;
  padding: 8px;
`;

export const HeaderActions = ({
  venue,
  isRetail,
  dateRange,
  isDateRangeIntersectsWithAllowedRange,
  query,
  handleDateRangeChange,
  setIsBarCodesModalOpen,
}: HeaderActionsProps) => {
  const { locale, formatMessage } = useIntl();
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [downloadMenuContent, setDownloadMenuContent] = useState<'menu' | 'receipts' | 'csv'>('menu');

  return (
    <HeaderActionsContainer>
      <Popover
        content={
          <FilterDateRangePicker
            onDateRangeChange={handleDateRangeChange}
            placeholder={formatMessage({ id: 'purchases.choose-dates' })}
            value={dateRange}
            timezone={venue.timezone}
          />
        }
        showOnClick
        showOnHover={false}
        renderArrow={false}
        placement="bottom"
      >
        <DateRangePickerInput
          value={`${
            dateRange?.from
              ? DateTime.fromFormat(dateRange.from, 'yyyy-MM-dd', { zone: venue.timezone }).toLocaleString(
                  DateTime.DATE_SHORT,
                  {
                    locale,
                  },
                )
              : ''
          } - ${
            dateRange?.to
              ? DateTime.fromFormat(dateRange.to, 'yyyy-MM-dd', { zone: venue.timezone }).toLocaleString(
                  DateTime.DATE_SHORT,
                  {
                    locale,
                  },
                )
              : ''
          }`}
          icon={<Calendar />}
          readOnly
        />
      </Popover>

      <HeaderButtons>
        <DeliveryTypeSwitcher venue={venue} />

        <Popover
          placement="bottom-end"
          onClickOutside={() => setIsDownloadMenuOpen(false)}
          show={isDownloadMenuOpen}
          showOnHover={false}
          renderArrow={false}
          content={
            downloadMenuContent === 'receipts' ? (
              <StyledGetReceipts
                initialDateRange={dateRange}
                onClose={() => setIsDownloadMenuOpen(false)}
                venueId={venue.id}
              />
            ) : downloadMenuContent === 'csv' && dateRange ? (
              <StyledDownloadCSV
                venueId={venue.id}
                timezone={venue.timezone}
                isDownloadingDisabled={isDateRangeIntersectsWithAllowedRange || query.filters === 'active'}
                dateRange={dateRange}
                query={query}
                onClose={() => setIsDownloadMenuOpen(false)}
              />
            ) : (
              <DownloadMenuContainer>
                <MenuItemGroup>
                  {dateRange && (
                    <MenuActionItem
                      onClick={() => {
                        telemetryMerchantAdminPurchasesInteractionDownloadCsv();
                        setDownloadMenuContent('csv');
                      }}
                      variant="black"
                    >
                      <FormattedMessage id="purchases.download-csv" />
                    </MenuActionItem>
                  )}
                  <MenuActionItem
                    variant="black"
                    onClick={() => {
                      telemetryMerchantAdminPurchasesInteractionGetReceipts();
                      setDownloadMenuContent('receipts');
                    }}
                  >
                    <FormattedMessage id="purchases.getReceipts" />
                  </MenuActionItem>
                  {isRetail && (
                    <MenuActionItem
                      variant="black"
                      onClick={() => {
                        setIsDownloadMenuOpen(false);
                        setIsBarCodesModalOpen(true);
                      }}
                    >
                      <FormattedMessage id="purchases.barcodes.action" />
                    </MenuActionItem>
                  )}
                </MenuItemGroup>
              </DownloadMenuContainer>
            )
          }
        >
          <DownloadButton
            onClick={() => {
              if (!isDownloadMenuOpen) {
                setDownloadMenuContent('menu');
                setIsDownloadMenuOpen(true);
              } else if (downloadMenuContent === 'menu') {
                setIsDownloadMenuOpen(false);
              } else {
                setDownloadMenuContent('menu');
              }
            }}
            icon={<CaretDown />}
            iconPosition="right"
            size="small"
            variant="blue"
          >
            <FormattedMessage id="state.download" />
          </DownloadButton>
        </Popover>
      </HeaderButtons>
    </HeaderActionsContainer>
  );
};
